import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, FileArchive, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadPrivateApp } from "@/services/privateAppsApi";
import { getTenantId } from "@/services/apiClient";
import type { PkgUploadResult } from "@/types/api";

interface PrivateAppUploadProps {
  onUploaded: (application: PkgUploadResult["application"]) => void;
}

const MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB hard cap
const WARN_BYTES = 500 * 1024 * 1024; // 500 MB soft warn

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const PrivateAppUpload = ({ onUploaded }: PrivateAppUploadProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSet = useCallback((picked: File | null | undefined) => {
    if (!picked) return;
    if (!picked.name.toLowerCase().endsWith(".pkg")) {
      toast.error("Only .pkg files are supported.");
      return;
    }
    if (picked.size === 0) {
      toast.error(
        `"${picked.name}" is empty (0 bytes). Pick the original .pkg from your local disk.`
      );
      return;
    }
    if (picked.size > MAX_BYTES) {
      toast.error(`File is too large (${formatBytes(picked.size)}). Maximum is 2 GB.`);
      return;
    }
    if (picked.size > WARN_BYTES) {
      toast.warning(`Large file (${formatBytes(picked.size)}) — upload may take a while.`);
    }
    setFile(picked);
  }, []);

  const handleBrowse = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSet(e.target.files?.[0]);
    // Reset so picking the same file again still triggers onChange
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSet(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemove = () => setFile(null);

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const tenantId = getTenantId() ?? undefined;
      const result = await uploadPrivateApp(file, tenantId);
      toast.success(`Uploaded "${result.application.name ?? file.name}"`);
      onUploaded(result.application);
      setFile(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {!file ? (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload .pkg file"
          onClick={handleBrowse}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleBrowse();
            }
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
            transition-colors bg-card
            ${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}
          `}
        >
          <Upload className="mx-auto h-10 w-10 text-muted-foreground mb-4" aria-hidden="true" />
          <p className="text-base font-medium text-foreground">
            Drop your .pkg file here, or click to browse
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            macOS installer packages only · up to 2 GB
          </p>
          <input
            ref={inputRef}
            type="file"
            accept=".pkg"
            className="hidden"
            onChange={handleFileChange}
            data-testid="private-app-file-input"
          />
        </div>
      ) : (
        <div className="border rounded-lg p-4 bg-card flex items-center gap-3">
          <FileArchive className="h-8 w-8 text-primary flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{file.name}</p>
            <p className="text-sm text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          {!isUploading && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {isUploading && (
        <div className="space-y-2" role="status" aria-live="polite">
          <Progress value={undefined} className="h-2" />
          <p className="text-sm text-muted-foreground text-center">
            Uploading… please keep this tab open.
          </p>
        </div>
      )}

      {file && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleRemove} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
